// src/components/GenerateAdMarketingVideoModal.jsx
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import { RefreshCcw, X, Loader2, Plus, Trash2 } from "lucide-react";
import AspectRatioPicker from "./AspectRatioPicker";
import InfiniteScroll from "react-infinite-scroll-component";
import Loader from "./Loader";

const steps = [
  { number: 1, label: "Assets" },
  { number: 2, label: "Script" },
  { number: 3, label: "Video Format" },
  { number: 4, label: "Voice" },
  { number: 5, label: "Avatar" },
  { number: 6, label: "End card" },
];

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://ecom-ads-ai-backend-production.up.railway.app";
const TOPVIEW_UID = process.env.REACT_APP_TOPVIEW_UID || "your-topview-uid";
const PAGE_SIZE = 32;
const SCRIPT_CHAR_LIMIT = 400;

const ALLOWED_IMAGE_EXTENSIONS = [
  ".png",
  ".jpeg",
  ".jpg",
  ".webp",
  ".heic",
  ".heif",
];

const LANGUAGE_FLAG_MAP = {
  en: "🇺🇸",
  ar: "🇸🇦",
  bg: "🇧🇬",
  hr: "🇭🇷",
  cs: "🇨🇿",
  da: "🇩🇰",
  nl: "🇳🇱",
  fil: "🇵🇭",
  fi: "🇫🇮",
  fr: "🇫🇷",
  de: "🇩🇪",
  el: "🇬🇷",
  hi: "🇮🇳",
  hu: "🇭🇺",
  id: "🇮🇩",
  it: "🇮🇹",
  ja: "🇯🇵",
  ko: "🇰🇷",
  ms: "🇲🇾",
  nb: "🇳🇴",
  pl: "🇵🇱",
  pt: "🇵🇹",
  ro: "🇷🇴",
  ru: "🇷🇺",
  "zh-CN": "🇨🇳",
  sk: "🇸🇰",
  es: "🇪🇸",
  sv: "🇸🇪",
  "zh-Hant": "🇹🇼",
  tr: "🇹🇷",
  uk: "🇺🇦",
  vi: "🇻🇳",
  th: "🇹🇭",
};

// Per-project assets
const productAssetsCache = {};
// Per-project meta
const projectMetaCache = {};
// Voice filters
const cache = { voiceFilters: null };
// Captions list
const captionsCache = { list: null };

const UUID_V4_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuidV4(value) {
  return UUID_V4_REGEX.test(String(value || ""));
}

function normalizeAssets(arr) {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((item, i) => {
      if (typeof item === "string") return { id: `img-${i}`, url: item };
      if (item && typeof item === "object") {
        const url = item.url || item.assetUrl || item.src || "";
        return url ? { id: item.id || item.assetId || `img-${i}`, url } : null;
      }
      return null;
    })
    .filter(Boolean);
}

function clampScript(text, limit = SCRIPT_CHAR_LIMIT) {
  return String(text || "").replace(/\s+/g, " ").trim().slice(0, limit);
}

export default function GenerateAdMarketingVideoModal({
  projectId,
  projectAssets: projectAssetsProp = [],
  productLink = "",
  onClose,
  refreshVideos,
}) {
  const { getToken } = useAuth();
  const modalRef = useRef(null);

  const [step, setStep] = useState(1);
  const [productAssets, setProductAssets] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [productName, setProductName] = useState("");
  const [productDesc, setProductDesc] = useState("");
  const [assetsLoading, setAssetsLoading] = useState(false);

  // ✅ Step 1 manual upload (same functionality, new tile UI)
  const [step1UploadedAsset, setStep1UploadedAsset] = useState(null); // { id, url } | null
  const [step1UploadLoading, setStep1UploadLoading] = useState(false);
  const step1FileInputRef = useRef(null);

  const [script, setScript] = useState("");
  const [generatingScript, setGeneratingScript] = useState(false);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState("9:16");
  const scriptLength = script.length;
  const scriptNearLimit = scriptLength >= 320;

  const [voices, setVoices] = useState([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState(null);
  const [allLanguages, setAllLanguages] = useState([]);
  const [allVoiceGenders, setAllVoiceGenders] = useState([]);
  const [allAges, setAllAges] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedVoiceGender, setSelectedVoiceGender] = useState("");
  const [selectedAge, setSelectedAge] = useState("");
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [voicePage, setVoicePage] = useState(1);
  const [hasMoreVoices, setHasMoreVoices] = useState(true);

  const [avatars, setAvatars] = useState([]);
  const [selectedAvatarId, setSelectedAvatarId] = useState(null);
  const [allAvatarGenders, setAllAvatarGenders] = useState([]);
  const [selectedGender, setSelectedGender] = useState("");
  const [avatarsLoading, setAvatarsLoading] = useState(false);
  const [avatarPage, setAvatarPage] = useState(1);
  const [hasMoreAvatars, setHasMoreAvatars] = useState(true);

  const [captions, setCaptions] = useState([]);
  const [selectedCaptionId, setSelectedCaptionId] = useState("");
  const [captionsLoading, setCaptionsLoading] = useState(false);
  const [captionError, setCaptionError] = useState("");

  const [selectedEndcardAssetId, setSelectedEndcardAssetId] = useState(null);
  const [endcardUploads, setEndcardUploads] = useState([]);
  const [endcardUploadLoading, setEndcardUploadLoading] = useState(false);
  const endcardFileInputRef = useRef(null);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const currentlyPlayingAudioRef = useRef(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);

  const onVoicePlay = useCallback((audioRef, voiceId) => {
    if (
      currentlyPlayingAudioRef.current &&
      currentlyPlayingAudioRef.current !== audioRef.current
    ) {
      try {
        currentlyPlayingAudioRef.current.pause();
      } catch {
        // ignore
      }
    }
    currentlyPlayingAudioRef.current = audioRef.current;
    setPlayingAudioId(voiceId);
  }, []);

  const onVoicePause = useCallback(() => {
    if (currentlyPlayingAudioRef.current) {
      try {
        currentlyPlayingAudioRef.current.pause();
      } catch {
        // ignore
      }
    }
    currentlyPlayingAudioRef.current = null;
    setPlayingAudioId(null);
  }, []);

  const filteredAssets = useMemo(
    () =>
      productAssets.filter(({ url }) => {
        if (!url) return false;
        const lowerUrl = url.toLowerCase();
        return ALLOWED_IMAGE_EXTENSIONS.some((ext) => lowerUrl.endsWith(ext));
      }),
    [productAssets]
  );

  const selectedEndcardAsset = useMemo(
    () =>
      selectedEndcardAssetId
        ? endcardUploads.find((a) => a.id === selectedEndcardAssetId) || null
        : null,
    [endcardUploads, selectedEndcardAssetId]
  );

  const isAssetsInitialLoading =
    assetsLoading &&
    !error &&
    filteredAssets.length === 0 &&
    productAssets.length === 0;
  const isAssetsReloadLoading =
    assetsLoading &&
    !error &&
    (filteredAssets.length > 0 || productAssets.length > 0);

  const isVoicesInitialLoading =
    step === 4 && voicesLoading && !error && voices.length === 0;
  const isAvatarsInitialLoading =
    avatarsLoading && !error && avatars.length === 0;

  // ✅ Selection order map for numbered overlays (1..10)
  const selectedOrderMap = useMemo(() => {
    const map = {};
    selectedAssets.forEach((a, idx) => {
      if (a?.id) map[String(a.id)] = idx + 1;
    });
    return map;
  }, [selectedAssets]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      setAssetsLoading(true);
      setError(null);
      try {
        if (Array.isArray(projectAssetsProp) && projectAssetsProp.length > 0) {
          if (!isMounted) return;
          const normalized = normalizeAssets(projectAssetsProp);
          setProductAssets(normalized);
          if (projectId) {
            productAssetsCache[projectId] = normalized;
          }
        } else if (projectId) {
          if (productAssetsCache[projectId]) {
            if (!isMounted) return;
            setProductAssets(productAssetsCache[projectId]);
          } else {
            const token = await getToken();
            const res = await axios.get(
              `${BACKEND_URL}/api/projects/${projectId}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );
            if (!isMounted) return;
            const analyzed = normalizeAssets([
              ...(res.data?.images || []),
              ...(res.data?.videos || []),
            ]);
            setProductAssets(analyzed);
            productAssetsCache[projectId] = analyzed;
          }
        } else if (isMounted) {
          setProductAssets([]);
        }
      } catch {
        if (isMounted) setError("Failed to load assets.");
      } finally {
        if (isMounted) setAssetsLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [projectId, projectAssetsProp, getToken]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!projectId) return;

      try {
        if (projectMetaCache[projectId]) {
          if (ignore) return;
          const { name, desc } = projectMetaCache[projectId];
          setProductName(name);
          setProductDesc(desc);
          return;
        }

        const token = await getToken();
        const projectRes = await axios.get(
          `${BACKEND_URL}/api/projects/${projectId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (ignore) return;
        const project = projectRes.data || {};
        const name = project.product_name || "";
        const the_desc = project.description || "";
        setProductName(name);
        setProductDesc(the_desc);
        projectMetaCache[projectId] = { name, desc: the_desc };
      } catch {
        if (!ignore) {
          setProductName("");
          setProductDesc("");
        }
      }
    })();
    return () => {
      ignore = true;
    };
  }, [projectId, getToken]);

  useEffect(() => {
    const options = [
      { value: "male", label: "Male" },
      { value: "female", label: "Female" },
    ];
    setAllAvatarGenders(options);
  }, []);

  useEffect(() => {
    async function fetchVoiceFilters() {
      if (cache.voiceFilters) {
        setAllLanguages(cache.voiceFilters.languages);
        setAllVoiceGenders(cache.voiceFilters.genders);
        setAllAges(cache.voiceFilters.ages);
        return;
      }
      try {
        const token = await getToken();
        const res = await axios.get(
          `${BACKEND_URL}/api/topview/voices/filters`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Topview-Uid": TOPVIEW_UID,
            },
          }
        );
        const languages = res.data.languages || [];
        const genders = res.data.genders || [];
        const ages = res.data.ages || [];
        setAllLanguages(languages);
        setAllVoiceGenders(genders);
        setAllAges(ages);
        cache.voiceFilters = {
          languages,
          genders,
          ages,
        };
      } catch {
        setError("Failed to load voice filters.");
      }
    }
    fetchVoiceFilters();
  }, [getToken]);

  const fetchVoices = useCallback(
    async (page = 1, append = false) => {
      setVoicesLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const res = await axios.get(`${BACKEND_URL}/api/topview/voices`, {
          params: {
            pageNo: page,
            pageSize: PAGE_SIZE,
            language: selectedLanguage || undefined,
            gender: selectedVoiceGender || undefined,
            age: selectedAge || undefined,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            "Topview-Uid": TOPVIEW_UID,
          },
        });
        const newVoices = res.data || [];

        setVoices((prev) => (append ? [...prev, ...newVoices] : newVoices));
        setHasMoreVoices(newVoices.length === PAGE_SIZE);
        setVoicePage(page);
      } catch {
        setError("Failed to load voices.");
      } finally {
        setVoicesLoading(false);
      }
    },
    [getToken, selectedLanguage, selectedVoiceGender, selectedAge]
  );

  useEffect(() => {
    fetchVoices(1, false);
  }, [selectedLanguage, selectedVoiceGender, selectedAge, fetchVoices]);

  const loadMoreVoices = () => {
    if (!voicesLoading && hasMoreVoices) {
      fetchVoices(voicePage + 1, true);
    }
  };

  const fetchAvatars = useCallback(
    async (page = 1, append = false) => {
      setAvatarsLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const res = await axios.get(`${BACKEND_URL}/api/topview/avatars`, {
          params: {
            pageNo: page,
            pageSize: PAGE_SIZE,
            gender: selectedGender || undefined,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            "Topview-Uid": TOPVIEW_UID,
          },
        });
        const newAvatars = res.data || [];

        setAvatars((prev) => (append ? [...prev, ...newAvatars] : newAvatars));
        setHasMoreAvatars(newAvatars.length === PAGE_SIZE);
        setAvatarPage(page);
      } catch {
        setError("Failed to load avatars.");
      } finally {
        setAvatarsLoading(false);
      }
    },
    [getToken, selectedGender]
  );

  useEffect(() => {
    fetchAvatars(1, false);
  }, [selectedGender, fetchAvatars]);

  const loadMoreAvatars = () => {
    if (!avatarsLoading && hasMoreAvatars) {
      fetchAvatars(avatarPage + 1, true);
    }
  };

  useEffect(() => {
    if (step !== 4) return;
    let mounted = true;

    async function fetchCaptions() {
      setCaptionsLoading(true);
      setCaptionError("");

      if (captionsCache.list && captionsCache.list.length > 0) {
        const cached = captionsCache.list;
        setCaptions(cached);
        setSelectedCaptionId((prev) => prev || cached[0]?.id || "");
        setCaptionsLoading(false);
        return;
      }

      try {
        const token = await getToken();

        // ✅ switched to backend route that uses TopView key-pool fallback
        const res = await axios.get(
          `${BACKEND_URL}/api/videos/marketing/captions`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!mounted) return;

        const list = Array.isArray(res.data?.result)
          ? res.data.result
          : Array.isArray(res.data)
          ? res.data
          : [];

        const mapped = list.map((c, idx) => {
          const rawId = String(c.captionId || c.id || idx);
          const labelIndex = String(idx + 1).padStart(2, "0");
          return {
            id: rawId,
            name: `Style ${labelIndex}`,
            thumbnail:
              c.thumbnail ||
              c.thumbnailUrl ||
              c.captionImageUrl ||
              c.imageUrl ||
              c.previewImageUrl ||
              c.iconUrl ||
              "",
          };
        });

        captionsCache.list = mapped;

        setCaptions(mapped);
        if (mapped.length > 0) {
          setSelectedCaptionId((prev) => prev || mapped[0].id);
        }
      } catch {
        setCaptionError("Failed to load captions (optional).");
        setCaptions([]);
        setSelectedCaptionId("");
      } finally {
        setCaptionsLoading(false);
      }
    }

    fetchCaptions();

    return () => {
      mounted = false;
    };
  }, [getToken, step]);

  const handleRegenerateScript = useCallback(async () => {
    setGeneratingScript(true);
    setError(null);
    try {
      const token = await getToken();
      const imageUrl = productAssets.length > 0 ? productAssets[0].url : "";
      const response = await axios.post(
        `${BACKEND_URL}/api/topview/script/generate`,
        {
          productName,
          productDescription: productDesc,
          productImageUrl: imageUrl,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const lines = response.data?.script || [];
      setScript(clampScript(lines.map((l) => l.text).join("\n")));
    } catch {
      setError("Failed to generate script.");
      setScript("");
    } finally {
      setGeneratingScript(false);
    }
  }, [getToken, productAssets, productDesc, productName]);

  useEffect(() => {
    if (!script && !generatingScript && productName && productDesc) {
      handleRegenerateScript();
    }
  }, [
    script,
    generatingScript,
    productName,
    productDesc,
    handleRegenerateScript,
  ]);

  const toggleAsset = (asset) => {
    setSelectedAssets((prev) => {
      const exists = prev.some((a) => a.id === asset.id);
      if (exists) return prev.filter((a) => a.id !== asset.id);
      if (prev.length >= 10) return prev;
      return [...prev, asset];
    });
  };

  const handleVoiceSelect = (voiceId) => {
    setSelectedVoiceId(voiceId ? String(voiceId) : null);
  };

  const handleAvatarSelect = useCallback((avatarId) => {
    setSelectedAvatarId(avatarId ? String(avatarId) : null);
  }, []);

  const isNextDisabled = (() => {
    if (step === 1) return selectedAssets.length < 1;
    if (step === 2) return !script && !generatingScript;
    if (step === 4) return !selectedVoiceId;
    return false;
  })();

  const isGenerateDisabled = loading || generatingScript || !selectedAvatarId;

  // ✅ Step 1 manual upload handler (same behavior)
  const handleStep1Upload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setStep1UploadLoading(true);
    setError(null);

    try {
      if (file.size > 20 * 1024 * 1024) {
        setError("Image is too large. Please upload a file under 20MB.");
        return;
      }

      const hasUploadedSelected =
        step1UploadedAsset &&
        selectedAssets.some((a) => a.id === step1UploadedAsset.id);

      if (selectedAssets.length >= 10 && !hasUploadedSelected) {
        setError("You already selected 10 assets. Remove one before uploading.");
        return;
      }

      const token = await getToken();
      const formData = new FormData();
      formData.append("file", file);

      // Reuse existing upload endpoint (same as End card upload)
      const { data } = await axios.post(
        `${BACKEND_URL}/api/videos/marketing/endcard/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const url = data?.url;
      if (url) {
        const newAsset = {
          id: `asset-upload-${Date.now()}`,
          url,
        };

        setSelectedAssets((prev) => {
          const withoutOld = step1UploadedAsset
            ? prev.filter((a) => a.id !== step1UploadedAsset.id)
            : prev;

          if (withoutOld.length >= 10) return withoutOld;

          const exists = withoutOld.some((a) => a.id === newAsset.id);
          if (exists) return withoutOld;

          return [newAsset, ...withoutOld];
        });

        setStep1UploadedAsset(newAsset);
      }
    } catch {
      setError("Failed to upload image.");
    } finally {
      setStep1UploadLoading(false);
      event.target.value = "";
    }
  };

  const handleEndcardUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setEndcardUploadLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await axios.post(
        `${BACKEND_URL}/api/videos/marketing/endcard/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const url = data?.url;
      if (url) {
        const newAsset = {
          id: `endcard-upload-${Date.now()}`,
          url,
        };
        setEndcardUploads((prev) => [newAsset, ...prev]);
        setSelectedEndcardAssetId(newAsset.id);
      }
    } catch {
      setError("Failed to upload end card image.");
    } finally {
      setEndcardUploadLoading(false);
      event.target.value = "";
    }
  };

  const StepFooter = ({ left, right }) => (
    <div className="absolute bottom-0 left-0 w-full bg-[#191930] border-t border-[#2B2B4A] flex justify-between items-center px-8 py-4 z-20">
      <div>{left}</div>
      <div className="flex items-center gap-3">{right}</div>
    </div>
  );

  const isUploadedSelected = useMemo(() => {
    if (!step1UploadedAsset) return false;
    return selectedAssets.some((a) => a.id === step1UploadedAsset.id);
  }, [selectedAssets, step1UploadedAsset]);

  const isUploadTileDisabled =
    !step1UploadedAsset && selectedAssets.length >= 10;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-2 py-4">
      <div
        ref={modalRef}
        className="relative bg-[#191930] w-[980px] max-w-[98vw] h-[86vh] rounded-2xl shadow-2xl flex flex-col min-h-[520px] overflow-hidden"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-white text-black p-2 rounded-full hover:bg-gray-100 z-10"
          aria-label="Close modal"
          disabled={loading}
          type="button"
        >
          <X size={22} />
        </button>

        <div className="flex items-center gap-2 px-8 pt-7 pb-2">
          {steps.map(({ number, label }) => (
            <React.Fragment key={number}>
              <span
                className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold
                  ${
                    step === number
                      ? "bg-pink-600 text-white"
                      : "bg-[#29294a] text-gray-400"
                  }`}
              >
                {number}
              </span>
              <span className="text-base sm:text-lg text-white font-semibold mr-8">
                {label}
              </span>
            </React.Fragment>
          ))}
        </div>

        <div
          id="ad-marketing-video-modal-scroll"
          className="flex-1 flex flex-col px-8 pt-2 pb-32 min-h-0 overflow-y-auto"
        >
          {step === 1 && (
            <>
              <p className="mb-4 text-gray-300 flex items-center gap-4">
                Select product images to include in your video (max 10).
                <span className="text-gray-400 text-sm flex items-center gap-3">
                  Selected {selectedAssets.length} / 10
                  {/* ✅ Visual count indicator (fills as you select) */}
                  <span className="flex items-center gap-1">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <span
                        key={i}
                        className={`w-2.5 h-2.5 rounded-full ${
                          i < selectedAssets.length
                            ? "bg-pink-500"
                            : "bg-[#2B2B4A]"
                        }`}
                      />
                    ))}
                  </span>
                </span>
              </p>

              <input
                ref={step1FileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleStep1Upload}
              />

              {isAssetsInitialLoading ? (
                <div className="flex items-center justify-center py-10 sm:py-16 select-none">
                  <Loader />
                </div>
              ) : isAssetsReloadLoading ? (
                <div className="grid grid-cols-5 gap-4">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div
                      key={i}
                      className="rounded-lg border border-[#2B2B4A] bg-[#23233a] aspect-[4/5] h-[150px] md:h-[170px] animate-pulse"
                    />
                  ))}
                </div>
              ) : filteredAssets.length === 0 ? (
                <div className="text-white/70 text-center py-10 select-none">
                  No compatible product images found. Analyze your product to
                  extract images first.
                </div>
              ) : (
                <div className="rounded-lg border border-[#23233a] bg-[#191930] px-4 py-3">
                  <div className="grid grid-cols-5 gap-4">
                    {/* ✅ Upload tile (same size as assets; becomes a normal asset once uploaded) */}
                    <div
                      className={`relative rounded-lg overflow-hidden border-2 group transition
                        ${
                          step1UploadedAsset
                            ? isUploadedSelected
                              ? "border-pink-500 ring-2 ring-pink-600 cursor-pointer"
                              : "border-transparent hover:border-pink-400 cursor-pointer"
                            : isUploadTileDisabled
                            ? "border-[#2B2B4A] opacity-50 cursor-not-allowed"
                            : "border-[#3A3A4A] border-dashed hover:border-pink-400 cursor-pointer"
                        }`}
                      onClick={() => {
                        if (loading || step1UploadLoading) return;

                        if (step1UploadedAsset) {
                          // Click behaves like a normal asset toggle
                          if (!isUploadedSelected && selectedAssets.length >= 10)
                            return;
                          toggleAsset(step1UploadedAsset);
                          return;
                        }

                        if (isUploadTileDisabled) return;
                        step1FileInputRef.current?.click();
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();

                          if (loading || step1UploadLoading) return;

                          if (step1UploadedAsset) {
                            if (
                              !isUploadedSelected &&
                              selectedAssets.length >= 10
                            )
                              return;
                            toggleAsset(step1UploadedAsset);
                            return;
                          }

                          if (isUploadTileDisabled) return;
                          step1FileInputRef.current?.click();
                        }
                      }}
                      aria-label={
                        step1UploadedAsset
                          ? "Uploaded asset"
                          : isUploadTileDisabled
                          ? "Upload disabled (max assets selected)"
                          : "Upload image"
                      }
                    >
                      <div className="aspect-[4/5] bg-black relative">
                        {step1UploadedAsset ? (
                          <img
                            className="object-cover w-full h-full"
                            src={step1UploadedAsset.url}
                            alt="Uploaded asset"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-[#151529]">
                            <div className="w-12 h-12 rounded-full bg-[#23233a] border border-[#2B2B4A] flex items-center justify-center mb-3">
                              <Plus size={22} className="text-white" />
                            </div>
                            <div className="text-white font-semibold text-sm">
                              Upload image
                            </div>
                            <div className="text-gray-400 text-[11px] mt-1 px-3 text-center">
                              JPG, PNG, WEBP
                            </div>
                          </div>
                        )}

                        {/* ✅ Selected order badge */}
                        {step1UploadedAsset &&
                          selectedOrderMap[String(step1UploadedAsset.id)] && (
                            <div className="absolute top-2 left-2 z-10 w-7 h-7 rounded-full bg-pink-600 text-white text-sm font-bold flex items-center justify-center shadow-lg border border-white/10">
                              {selectedOrderMap[String(step1UploadedAsset.id)]}
                            </div>
                          )}

                        {step1UploadLoading && (
                          <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                            <Loader2 className="animate-spin text-white" />
                          </div>
                        )}

                        {/* subtle hover tint like other assets */}
                        <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                      </div>

                      {/* Actions (only when uploaded) */}
                      {step1UploadedAsset && (
                        <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition">
                          <button
                            type="button"
                            className="px-2.5 py-1.5 rounded-md bg-black/70 hover:bg-black/85 text-white text-xs font-semibold border border-white/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (loading || step1UploadLoading) return;
                              step1FileInputRef.current?.click();
                            }}
                            aria-label="Replace uploaded image"
                            disabled={loading || step1UploadLoading}
                          >
                            Replace
                          </button>
                          <button
                            type="button"
                            className="p-2 rounded-md bg-black/70 hover:bg-black/85 text-white border border-white/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (loading) return;

                              const uploadedId = step1UploadedAsset.id;
                              setStep1UploadedAsset(null);
                              setSelectedAssets((prev) =>
                                prev.filter((a) => a.id !== uploadedId)
                              );
                            }}
                            aria-label="Remove uploaded image"
                            disabled={loading}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>

                    {filteredAssets.map((asset) => {
                      const selected = selectedAssets.some(
                        (a) => a.id === asset.id
                      );
                      const disabled = !selected && selectedAssets.length >= 10;
                      const selectedIndex = selectedOrderMap[String(asset.id)];
                      return (
                        <div
                          key={asset.id}
                          className={`relative rounded-lg overflow-hidden border-2 cursor-pointer group transition
                            ${
                              selected
                                ? "border-pink-500 ring-2 ring-pink-600"
                                : "border-transparent hover:border-pink-400"
                            } ${
                            disabled ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                          onClick={() => !disabled && toggleAsset(asset)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              if (!disabled) toggleAsset(asset);
                            }
                          }}
                        >
                          <div className="aspect-[4/5] bg-black relative">
                            <img
                              className="object-cover w-full h-full"
                              src={asset.url}
                              alt="Asset"
                              loading="lazy"
                            />

                            {/* ✅ Selected order badge */}
                            {selected && selectedIndex ? (
                              <div className="absolute top-2 left-2 z-10 w-7 h-7 rounded-full bg-pink-600 text-white text-sm font-bold flex items-center justify-center shadow-lg border border-white/10">
                                {selectedIndex}
                              </div>
                            ) : null}
                          </div>

                          <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

{step === 2 && (
            <div className="mt-2">
              <div className="flex items-center mb-2">
                <p className="text-gray-300 flex-1">
                  AI-generated video script (you can edit).
                </p>
                <button
                  onClick={handleRegenerateScript}
                  className="ml-2 p-2 text-pink-400 hover:text-pink-300 rounded-full bg-[#232336] hover:bg-[#2c2c3e] transition disabled:opacity-60"
                  disabled={generatingScript}
                  aria-label="Regenerate Script"
                  type="button"
                >
                  <RefreshCcw
                    size={20}
                    className={generatingScript ? "animate-spin" : ""}
                  />
                </button>
              </div>

              <div className="mb-3 text-white/70 text-xs">
  Scripts are limited to {SCRIPT_CHAR_LIMIT} characters for this format.
</div>

              {error && (
                <div className="mb-2 text-red-500 font-semibold">{error}</div>
              )}

              <textarea
                className="w-full min-h-[140px] max-h-72 bg-[#1F1F42] rounded-md p-4 text-white border border-gray-600 focus:border-pink-500 focus:ring-1 focus:ring-pink-600 transition"
                value={
                  generatingScript
                    ? "AI is loading the message, please hold..."
                    : script
                }
                onChange={(e) => setScript(clampScript(e.target.value))}
                disabled={generatingScript}
                placeholder="The video script will appear here..."
                rows={7}
                maxLength={SCRIPT_CHAR_LIMIT}
              />

              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-white/60">
                  Maximum {SCRIPT_CHAR_LIMIT} characters
                </span>
                <span
                  className={`text-xs font-medium ${
                    scriptNearLimit ? "text-amber-300" : "text-white/60"
                  }`}
                >
                  {scriptLength} / {SCRIPT_CHAR_LIMIT}
                </span>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="mt-2">
              <p className="mb-4 text-gray-300">
                Select video aspect ratio. Your end card will automatically
                match this format.
              </p>
              <AspectRatioPicker
                selected={selectedAspectRatio}
                onSelect={setSelectedAspectRatio}
              />
            </div>
          )}

          {step === 4 && (
            <>
              {isVoicesInitialLoading ? (
                <div className="flex items-center justify-center py-10 sm:py-16 select-none">
                  <Loader />
                </div>
              ) : (
                <>
                  <h2 className="text-xl font-bold text-white mb-4">
                    Select Voice & Captions
                  </h2>

                  <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                    <div className="flex flex-wrap items-start gap-4">
                      <SingleSelectDropdown
                        options={allLanguages}
                        value={selectedLanguage}
                        onChange={setSelectedLanguage}
                        label="Language"
                        placeholder="All languages"
                        className="w-44"
                      />
                      <SingleSelectDropdown
                        options={allVoiceGenders}
                        value={selectedVoiceGender}
                        onChange={setSelectedVoiceGender}
                        label="Gender"
                        placeholder="All genders"
                        className="w-36"
                      />
                      <SingleSelectDropdown
                        options={allAges}
                        value={selectedAge}
                        onChange={setSelectedAge}
                        label="Age"
                        placeholder="All ages"
                        className="w-36"
                      />
                      <CaptionDropdown
                        captions={captions}
                        selectedCaptionId={selectedCaptionId}
                        onChange={setSelectedCaptionId}
                        loading={captionsLoading}
                        error={captionError}
                      />
                    </div>

                    <div className="flex flex-col items-end">
                      <span className="block text-xs mb-1 ml-1 opacity-0">
                        Clear
                      </span>
                      <button
                        onClick={() => {
                          setSelectedLanguage("");
                          setSelectedVoiceGender("");
                          setSelectedAge("");
                        }}
                        className="px-3 py-2 bg-pink-600 rounded-md text-white hover:bg-pink-700 text-sm"
                        aria-label="Clear voice filters"
                        type="button"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>

                  <InfiniteScroll
                    dataLength={voices.length}
                    next={loadMoreVoices}
                    hasMore={hasMoreVoices}
                    loader={
                      voicesLoading && hasMoreVoices ? (
                        <div className="w-full flex justify-center py-4">
                          <Loader />
                        </div>
                      ) : null
                    }
                    scrollableTarget="ad-marketing-video-modal-scroll"
                    scrollThreshold={0.85}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 pb-4">
                      {voices.map((voice) => {
                        const isSelected =
                          String(selectedVoiceId) === String(voice.id);
                        return (
                          <div
                            key={voice.id}
                            className={`relative rounded-lg border-2 transition cursor-pointer flex flex-col justify-between p-4 group bg-[#23233a]
                              ${
                                isSelected
                                  ? "border-pink-500 ring-2 ring-pink-600"
                                  : "border-transparent hover:border-pink-400"
                              }`}
                            onClick={() => handleVoiceSelect(voice.id)}
                            aria-label={`Select voice ${
                              voice.name || "Unnamed"
                            }`}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleVoiceSelect(voice.id);
                              }
                            }}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-2xl">
                                {LANGUAGE_FLAG_MAP[voice.language] || ""}
                              </span>
                              <span className="font-semibold text-white text-lg truncate">
                                {voice.name}
                              </span>
                            </div>
                            <div className="text-sm text-gray-300 mb-2 space-y-1">
                              <div>
                                <strong>Language:</strong>{" "}
                                {voice.language || "N/A"}
                              </div>
                              <div>
                                <strong>Accent:</strong>{" "}
                                {voice.accent || "N/A"}
                              </div>
                              <div>
                                <strong>Gender:</strong>{" "}
                                {voice.gender || "N/A"}
                              </div>
                              <div>
                                <strong>Age:</strong> {voice.age || "N/A"}
                              </div>
                            </div>

                            {voice.preview_url && (
                              <VoicePreviewButton
                                audioSrc={voice.preview_url}
                                voiceId={voice.id}
                                playingAudioId={playingAudioId}
                                onVoicePlay={onVoicePlay}
                                onVoicePause={onVoicePause}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </InfiniteScroll>
                </>
              )}
            </>
          )}

          {step === 5 && (
            <>
              {isAvatarsInitialLoading ? (
                <div className="flex items-center justify-center py-10 sm:py-16 select-none">
                  <Loader />
                </div>
              ) : (
                <>
                  <AvatarFilters
                    allAvatarGenders={allAvatarGenders}
                    selectedGender={selectedGender}
                    setSelectedGender={setSelectedGender}
                  />
                  <InfiniteScroll
                    dataLength={avatars.length}
                    next={loadMoreAvatars}
                    hasMore={hasMoreAvatars}
                    loader={
                      avatarsLoading && hasMoreAvatars ? (
                        <div className="w-full flex justify-center py-4">
                          <Loader />
                        </div>
                      ) : null
                    }
                    scrollableTarget="ad-marketing-video-modal-scroll"
                    scrollThreshold={0.85}
                  >
                    <div className="grid grid-cols-5 gap-4 pb-4">
                      {avatars.map((avatar) => {
                        const id = avatar.aiavatarId || avatar.id;
                        const isSelected =
                          String(selectedAvatarId) === String(id);
                        return (
                          <AvatarCard
                            key={id}
                            avatar={avatar}
                            isSelected={isSelected}
                            onSelect={handleAvatarSelect}
                          />
                        );
                      })}
                    </div>
                  </InfiniteScroll>
                </>
              )}
            </>
          )}

          {step === 6 && (
            <div className="mt-2 flex flex-col items-center">
              <p className="mb-2 text-gray-300 text-center">
                Optional end card image. This will be shown at the end of the
                video. If you skip this step, the video will end on the final
                frame.
              </p>
              <p className="mb-6 text-sm text-gray-400 text-center">
                Upload a single end card image to use for this video. It will
                automatically match the aspect ratio you selected.
              </p>

              <div className="flex justify-center w-full">
                <div
                  className="flex flex-col items-center justify-center border-2 border-dashed border-[#3A3A4A] rounded-2xl bg-[#191930] p-6"
                  style={{
                    width: 262.7,
                    height: 300,
                  }}
                >
                  {selectedEndcardAsset ? (
                    <>
                      <div className="flex-1 flex items-center justify-center w-full mb-4">
                        <img
                          src={selectedEndcardAsset.url}
                          alt="End card"
                          className="max-h-full max-w-full object-contain rounded-lg"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex flex-col items-center gap-2">
                        <span className="text-xs text-gray-300">
                          End card selected.
                        </span>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => endcardFileInputRef.current?.click()}
                            className="px-3 py-1.5 rounded-lg bg-pink-600 hover:bg-pink-700 text-white text-xs font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                            disabled={endcardUploadLoading || loading}
                          >
                            {endcardUploadLoading
                              ? "Uploading..."
                              : "Change image"}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedEndcardAssetId(null);
                              setEndcardUploads([]);
                            }}
                            className="px-3 py-1.5 rounded-lg bg-[#2B2B4A] hover:bg-[#3D3D6E] text-white text-xs font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                            disabled={loading}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-1 flex-col items-center justify-center w-full">
                      <span className="text-sm text-gray-300 text-center mb-3">
                        Upload an optional end card image
                      </span>
                      <button
                        type="button"
                        onClick={() => endcardFileInputRef.current?.click()}
                        className="px-4 py-2 rounded-lg bg-pink-600 hover:bg-pink-700 text-white text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                        disabled={endcardUploadLoading || loading}
                      >
                        {endcardUploadLoading ? "Uploading..." : "Upload image"}
                      </button>
                      <span className="text-[11px] text-gray-500 mt-3 text-center">
                        JPG, PNG, WEBP. Max 20MB.
                      </span>
                    </div>
                  )}

                  <input
                    ref={endcardFileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleEndcardUpload}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <StepFooter
          left={
            <button
              disabled={step === 1 || loading}
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              className="rounded-lg font-semibold px-6 py-2 bg-[#2B2B4A] hover:bg-[#3D3D6E] text-white disabled:opacity-60 disabled:cursor-not-allowed"
              type="button"
            >
              Previous
            </button>
          }
          right={
            step < 6 ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={loading || isNextDisabled}
                className="rounded-lg bg-pink-600 font-semibold px-6 py-2 hover:bg-pink-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                type="button"
              >
                Next
              </button>
            ) : (
              <button
                onClick={async () => {
                  if (
                    selectedAssets.length === 0 ||
                    !script ||
                    !selectedAvatarId ||
                    !selectedVoiceId ||
                    !selectedAspectRatio
                  ) {
                    setError("Please complete all steps before generating.");
                    return;
                  }
                  setError(null);
                  setLoading(true);

                  try {
                    const token = await getToken();
                    const normalizedScript = clampScript(script);
                    const scriptLines = normalizedScript
                      .split("\n")
                      .map((line) => ({ text: line.trim().slice(0, SCRIPT_CHAR_LIMIT) }))
                      .filter((l) => l.text);

                    const endcardPayloadValue = selectedEndcardAsset
                      ? isUuidV4(selectedEndcardAsset.id)
                        ? selectedEndcardAsset.id
                        : selectedEndcardAsset.url
                      : null;

                      const { data } = await axios.post(
                        `${BACKEND_URL}/api/videos/marketing/generate`,
                        {
                          projectId,
                          productLink,
                          assets: selectedAssets
                            .map((a) => (isUuidV4(a.id) ? a.id : a.url))
                            .filter(Boolean),
                          endcardAsset: endcardPayloadValue || null,
                          productName,
                          productDescription: productDesc,
                          avatar: selectedAvatarId,
                          voice: selectedVoiceId,
                          voiceLanguage: selectedLanguage,
                          aspectRatio: selectedAspectRatio,
                          isDiyScript: true,
                          scriptContents: scriptLines,
                          captionId: selectedCaptionId || null,
                        },
                        {
                          headers: {
                            Authorization: `Bearer ${token}`,
                            "Topview-Uid": TOPVIEW_UID,
                          },
                        }
                      );
                      
                      refreshVideos?.(data || null);
                      onClose?.();
                  } catch {
                    setError("Failed to generate video.");
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={isGenerateDisabled}
                className="rounded-lg bg-pink-600 font-semibold px-6 py-2 hover:bg-pink-700 text-white disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
                type="button"
              >
                {loading && (
                  <Loader2 className="animate-spin mr-2" size={18} />
                )}
                {loading ? "Generating..." : "Generate Video"}
              </button>
            )
          }
        />

        {error && (
          <div className="text-red-500 font-semibold text-center py-2 text-sm">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function VoicePreviewButton({
  audioSrc,
  voiceId,
  playingAudioId,
  onVoicePlay,
  onVoicePause,
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const handlePlayPause = (e) => {
    e.stopPropagation();
    const audioEl = audioRef.current;
    if (!audioEl) return;

    if (isPlaying) {
      try {
        audioEl.pause();
      } catch {
        // ignore
      }
      setIsPlaying(false);
      onVoicePause();
    } else {
      try {
        audioEl.currentTime = 0;
      } catch {
        // ignore
      }
      const playPromise = audioEl.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise
          .then(() => {
            setIsPlaying(true);
            onVoicePlay(audioRef, voiceId);
          })
          .catch(() => {
            setIsPlaying(false);
            onVoicePause();
          });
      } else {
        setIsPlaying(true);
        onVoicePlay(audioRef, voiceId);
      }
    }
  };

  useEffect(() => {
    if (playingAudioId !== voiceId && isPlaying) {
      const audioEl = audioRef.current;
      if (audioEl) {
        try {
          audioEl.pause();
        } catch {
          // ignore
        }
      }
      setIsPlaying(false);
    }
  }, [playingAudioId, voiceId, isPlaying]);

  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;

    const handleEnded = () => {
      setIsPlaying(false);
      onVoicePause();
    };

    audioEl.addEventListener("ended", handleEnded);
    return () => {
      audioEl.removeEventListener("ended", handleEnded);
      try {
        audioEl.pause();
      } catch {
        // ignore
      }
    };
  }, [audioSrc, onVoicePause]);

  return (
    <>
      <audio ref={audioRef} src={audioSrc} preload="none" />
      <button
        type="button"
        className="mt-auto bg-pink-600 hover:bg-pink-700 rounded-full p-2 flex items-center justify-center self-start"
        onClick={handlePlayPause}
        aria-label={isPlaying ? "Pause voice preview" : "Play voice preview"}
      >
        {isPlaying ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 9v6m4-6v6"
            />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-white"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M6 4l12 6-12 6V4z" />
          </svg>
        )}
      </button>
    </>
  );
}

const AvatarCard = React.memo(function AvatarCard({
  avatar,
  isSelected,
  onSelect,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [hasLoadedPreview, setHasLoadedPreview] = useState(false);
  const videoRef = useRef(null);

  const coverUrl = avatar.coverUrl || avatar.thumbnailUrl || "";
  const previewUrl = avatar.previewVideoUrl || avatar.previewUrl || "";
  const id = avatar.aiavatarId || avatar.id;

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    if (isHovered && hasLoadedPreview) {
      try {
        videoEl.currentTime = 0;
      } catch {
        // ignore
      }
      const playPromise = videoEl.play();
      if (playPromise && typeof playPromise.then === "function") {
        playPromise.catch(() => {
          // ignore autoplay errors
        });
      }
    } else {
      try {
        videoEl.pause();
        videoEl.currentTime = 0;
      } catch {
        // ignore
      }
    }
  }, [isHovered, hasLoadedPreview]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (!hasLoadedPreview) {
      setHasLoadedPreview(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div
      className={`relative rounded-lg overflow-hidden border-2 group transition cursor-pointer
        ${
          isSelected
            ? "border-pink-500 ring-2 ring-pink-600"
            : "border-transparent hover:border-pink-400"
        }`}
      aria-label={`Select avatar ${
        avatar.aiavatarName || avatar.name || "Unnamed"
      }`}
      onClick={() => onSelect(String(id))}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="aspect-[9/16] w-full h-full bg-black relative"
        style={{ minHeight: 160 }}
      >
        {coverUrl && (
          <img
            className={`w-full h-full object-cover transition-opacity duration-200 ${
              previewUrl && isHovered && hasLoadedPreview
                ? "opacity-0"
                : "opacity-100"
            }`}
            src={coverUrl}
            alt={avatar.aiavatarName || avatar.name || "Avatar Cover"}
            loading="lazy"
            draggable={false}
          />
        )}

        {previewUrl && hasLoadedPreview && (
          <video
            ref={videoRef}
            src={previewUrl}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-200 ${
              isHovered ? "opacity-100" : "opacity-0"
            }`}
            muted
            loop
            playsInline
            preload="none"
          />
        )}

        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-black/50 group-hover:bg-black/65 rounded-full p-2 flex items-center justify-center transition">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M6 4l12 6-12 6V4z" />
            </svg>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs text-center py-1 truncate z-10">
        {avatar.aiavatarName || avatar.name || "Unnamed"}
      </div>
    </div>
  );
});

function AvatarFilters({
  allAvatarGenders,
  selectedGender,
  setSelectedGender,
}) {
  return (
    <div className="flex gap-4 mb-4 flex-wrap items-center justify-start">
      <SingleSelectDropdown
        options={allAvatarGenders}
        value={selectedGender}
        onChange={setSelectedGender}
        label="Gender"
        placeholder="All genders"
        className="w-48"
      />
      <button
        onClick={() => {
          setSelectedGender("");
        }}
        className="ml-4 px-3 py-1.5 bg-pink-600 rounded-md text-white hover:bg-pink-700 text-sm self-end"
        aria-label="Clear avatar filters"
        type="button"
      >
        Clear Filters
      </button>
    </div>
  );
}

function CaptionDropdown({
  captions,
  selectedCaptionId,
  onChange,
  loading,
  error,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  if (loading) {
    return (
      <div className="flex flex-col gap-1 w-36">
        <label className="block text-xs text-gray-300 mb-1 ml-1">
          Caption style
        </label>
        <div className="text-gray-400 text-sm">Loading captions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-1 w-36">
        <label className="block text-xs text-gray-300 mb-1 ml-1">
          Caption style
        </label>
        <div className="text-red-400 text-xs mt-1">{error}</div>
      </div>
    );
  }

  if (!captions || captions.length === 0) {
    return (
      <div className="flex flex-col gap-1 w-36">
        <label className="block text-xs text-gray-300 mb-1 ml-1">
          Caption style
        </label>
        <div className="text-gray-400 text-sm">
          No captions available (optional).
        </div>
      </div>
    );
  }

  const selected = captions.find(
    (c) => String(c.id) === String(selectedCaptionId)
  );

  return (
    <div className="relative w-36" ref={ref}>
      <label className="block text-xs text-gray-300 mb-1 ml-1">
        Caption style
      </label>
      <button
        type="button"
        className={`
          flex w-full items-stretch px-3 py-2 rounded-lg bg-[#23233a] text-white border
          transition-all outline-none focus:ring-2 focus:ring-pink-600
          ${open ? "border-pink-500 ring-2 ring-pink-600" : "border-[#353550]"}
        `}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected ? (
          <div className="flex flex-col flex-1 min-w-0 gap-2 text-left">
            <span className="text-sm font-medium truncate">{selected.name}</span>
            {selected.thumbnail ? (
              <div className="w-full h-[56px] rounded-md border border-[#353550] bg-black/60 flex items-center justify-center overflow-hidden">
                <img
                  src={selected.thumbnail}
                  alt={selected.name}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
            ) : null}
          </div>
        ) : (
          <span className="text-gray-400 text-sm flex-1 text-left">
            Select caption style
          </span>
        )}
        <svg
          className={`ml-3 mt-1 flex-shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          width="18"
          height="18"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <ul
          className="absolute z-40 mt-1 w-full rounded-xl shadow-xl bg-[#212134] border border-[#353550] max-h-72 overflow-auto animate-fadeIn"
          role="listbox"
          tabIndex={-1}
        >
          {captions.map((c) => {
            const isSelected = String(selectedCaptionId) === String(c.id);
            return (
              <li
                key={c.id}
                className={`cursor-pointer px-3 py-2 text-sm select-none ${
                  isSelected
                    ? "bg-pink-600/10 text-pink-300"
                    : "hover:bg-[#2e2e45]"
                }`}
                onClick={() => {
                  onChange(String(c.id));
                  setOpen(false);
                }}
                role="option"
                aria-selected={isSelected}
              >
                <div className="flex flex-col gap-2 w-full text-left">
                  <span className="font-medium">{c.name}</span>
                  {c.thumbnail ? (
                    <div className="w-full h-[56px] rounded-md border border-[#353550] bg-black/60 flex items-center justify-center overflow-hidden">
                      <img
                        src={c.thumbnail}
                        alt={c.name}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function SingleSelectDropdown({
  options,
  value = "",
  onChange,
  label,
  placeholder = "Select...",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const getLabel = (val) => {
    if (!val) return null;
    const opt = options.find((o) => String(o.value) === String(val));
    return opt ? opt.label : val;
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <label className="block text-xs text-gray-300 mb-1 ml-1">{label}</label>
      <button
        type="button"
        className={`
          flex w-full min-h-[38px] items-center px-3 py-1.5 rounded-lg bg-[#23233a] text-white border
          transition-all outline-none focus:ring-2 focus:ring-pink-600
          ${
            open
              ? "border-pink-500 ring-2 ring-pink-600"
              : "border-[#353550]"
          }
        `}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div className="flex flex-1 items-center text-left">
          {value ? (
            <span className="truncate">{getLabel(value)}</span>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </div>
        <svg
          className={`ml-2 transition-transform ${open ? "rotate-180" : ""}`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          width="18"
          height="18"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {open && (
        <ul
          className="absolute z-40 mt-1 w-full rounded-xl shadow-xl bg-[#212134] border border-[#353550] max-h-56 overflow-auto animate-fadeIn"
          role="listbox"
          tabIndex={-1}
        >
          <li
            key="all"
            className={`cursor-pointer px-3 py-2 text-sm select-none ${
              !value
                ? "bg-pink-600/10 text-pink-300"
                : "hover:bg-[#2e2e45]"
            }`}
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
            role="option"
            aria-selected={!value}
          >
            All
          </li>
          {options.length === 0 && (
            <li className="p-3 text-gray-400">No options</li>
          )}
          {options.map((opt) => {
            const selected = String(value) === String(opt.value);
            return (
              <li
                key={opt.value}
                className={`cursor-pointer px-3 py-2 text-sm select-none ${
                  selected
                    ? "bg-pink-600/10 text-pink-300"
                    : "hover:bg-[#2e2e45]"
                }`}
                onClick={() => {
                  onChange(String(opt.value));
                  setOpen(false);
                }}
                role="option"
                aria-selected={selected}
              >
                {opt.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
